import { newImageElement } from "@excalidraw/element";

import type { FileId } from "@excalidraw/element/types";

import { getDataURL } from "../data/blob";

import type {
  BinaryFileData,
  ExcalidrawImperativeAPI,
  LibraryItems,
} from "../types";

export type EstudaImage = {
  id: string;
  url: string;
  width: number;
  height: number;
};

export const getEstudaImageUrl = (url: string) =>
  `https://img.estuda.com/img2/${url}`;

export const getFileFromLink = async (
  url: string,
  ownerWindow: Window & typeof globalThis,
) => {
  const response = await ownerWindow.fetch(getEstudaImageUrl(url));
  if (!response.ok) {
    throw new Error(`Unable to load Estuda image (${response.status})`);
  }

  const blob = await response.blob();
  return new ownerWindow.File([blob], "excalidraw", { type: blob.type });
};

export const loadEstudaLibraryFiles = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  images: readonly EstudaImage[],
  ownerWindow: Window & typeof globalThis,
) => {
  const files = await Promise.all(
    images.map(async (image): Promise<BinaryFileData | null> => {
      try {
        const response = await ownerWindow.fetch(image.url);
        if (!response.ok) {
          return null;
        }

        const blob = await response.blob();
        return {
          id: image.id as FileId,
          dataURL: await getDataURL(blob),
          mimeType: blob.type as BinaryFileData["mimeType"],
          created: Date.now(),
          lastRetrieved: Date.now(),
        };
      } catch {
        return null;
      }
    }),
  );

  excalidrawAPI.addFiles(
    files.filter((file): file is BinaryFileData => file !== null),
  );
};

export const convertToLibraryItems = (
  items: readonly Pick<EstudaImage, "id" | "width" | "height">[],
): LibraryItems =>
  items.map((item) => ({
    status: "estuda",
    elements: [
      newImageElement({
        type: "image",
        x: 0,
        y: 0,
        width: item.width,
        height: item.height,
        fileId: item.id as FileId,
        status: "saved",
        roughness: 4,
        strokeColor: "transparent",
        backgroundColor: "#000000",
      }),
    ],
    id: item.id,
    created: Date.now(),
  }));
